/* Rangetek hero carousel: vanilla, lightweight */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-hc-track]');
      this.viewport = root.querySelector('[data-hc-viewport]');
      this.slides = Array.from(root.querySelectorAll('[data-hc-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-hc-dot]'));
      this.toggleBtn = root.querySelector('[data-hc-toggle]');
      this.delay = parseInt(root.dataset.autoplay, 10) || 0;
      this.index = 0;
      this.timer = null;
      this.holds = new Set(); // hover, focus, touch, editor, hidden
      this.userPaused = reduceMotion.matches || !this.delay;
      if (this.slides.length < 2) return;
      this.bind();
      this.preload();
      this.syncToggle();
      this.schedule();
    }

    bind() {
      const r = this.root;
      r.querySelector('[data-hc-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      r.querySelector('[data-hc-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(+d.dataset.hcDot, true)));
      this.toggleBtn?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncToggle();
        this.schedule();
      });

      r.addEventListener('mouseenter', () => this.hold('hover'));
      r.addEventListener('mouseleave', () => this.release('hover'));
      r.addEventListener('focusin', () => this.hold('focus'));
      r.addEventListener('focusout', (e) => { if (!r.contains(e.relatedTarget)) this.release('focus'); });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.hold('hidden') : this.release('hidden')));

      r.addEventListener('keydown', (e) => {
        if (e.target.closest('input, textarea, select')) return;
        const map = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (e.key in map) { e.preventDefault(); this.go(map[e.key], true, true); }
      });

      // Swipe (touch only; vertical scroll stays native via touch-action: pan-y)
      let x0 = 0, y0 = 0, dx = 0, dragging = false, decided = false;
      this.viewport.addEventListener('touchstart', (e) => {
        this.hold('touch');
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; dx = 0; dragging = false; decided = false;
      }, { passive: true });
      this.viewport.addEventListener('touchmove', (e) => {
        const t = e.touches[0];
        const mx = t.clientX - x0, my = t.clientY - y0;
        if (!decided && (Math.abs(mx) > 8 || Math.abs(my) > 8)) { decided = true; dragging = Math.abs(mx) > Math.abs(my); }
        if (!dragging) return;
        dx = mx;
        this.track.classList.add('is-dragging');
        const w = this.viewport.clientWidth || 1;
        this.track.style.transform = `translate3d(${(-this.index * 100) + (dx / w) * 100}%,0,0)`;
      }, { passive: true });
      const end = () => {
        this.track.classList.remove('is-dragging');
        if (dragging) {
          const w = this.viewport.clientWidth || 1;
          if (Math.abs(dx) > Math.min(60, w * 0.18)) this.go(this.index + (dx < 0 ? 1 : -1), true);
          else this.go(this.index, false);
        }
        dragging = false;
        clearTimeout(this.touchTimer);
        this.touchTimer = setTimeout(() => this.release('touch'), this.delay || 5000);
      };
      this.viewport.addEventListener('touchend', end);
      this.viewport.addEventListener('touchcancel', end);

      // Theme editor
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.hold('editor'); this.go(i, false); }
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.root.contains(e.target)) this.release('editor');
      });
    }

    go(i, user = false, fromKeyboard = false) {
      const n = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.track.style.transform = `translate3d(${-this.index * 100}%,0,0)`;
      this.track.setAttribute('aria-live', user ? 'polite' : 'off');
      this.slides.forEach((s, k) => {
        const on = k === this.index;
        s.classList.toggle('is-active', on);
        s.toggleAttribute('inert', !on);
        if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((d, k) => (k === this.index ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current')));
      if (fromKeyboard && document.activeElement && !this.root.querySelector('.tekvo-hc__controls')?.contains(document.activeElement)) {
        this.dots[this.index]?.focus({ preventScroll: true });
      }
      this.schedule();
    }

    // Warm up off-screen slide images after the page has loaded so transitions never show an empty panel
    preload() {
      const run = () => this.root.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      if (document.readyState === 'complete') setTimeout(run, 300);
      else window.addEventListener('load', () => setTimeout(run, 300), { once: true });
    }

    hold(k) { this.holds.add(k); this.schedule(); }
    release(k) { this.holds.delete(k); this.schedule(); }

    syncToggle() {
      if (!this.toggleBtn) return;
      this.toggleBtn.setAttribute('aria-pressed', String(this.userPaused));
      this.toggleBtn.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
      if (!this.delay) this.toggleBtn.hidden = true;
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.userPaused || this.holds.size || !this.delay) return;
      this.timer = setTimeout(() => this.go(this.index + 1, false), this.delay);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-tekvo-hc]').forEach((el) => {
      if (el.__hc) return;
      el.__hc = new HeroCarousel(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
